/** @format */

import {useState, useCallback} from 'react';

import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {Trans, useTranslation} from 'react-i18next';

import {BlueButton} from 'components/buttons/BlueButton';

import {
  SidebarRoot,
  NewChatButtonContainer,
  ChatList,
  ChatItem,
  ChatTitle,
  ChatActions,
  DeleteDialog,
  RenameContainer,
} from './styles';

interface Chat {
  readonly id: string;
  readonly title?: string | null;
}

interface ChatSidebarProps {
  chats: readonly Chat[];
  activeChatId: string | null;
  loading: boolean;
  error: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onRenameChat: (chatId: string, title: string) => void;
  onDeleteChat: (chatId: string) => void;
}

const ChatSidebar = ({
  chats,
  activeChatId,
  loading,
  error,
  onSelectChat,
  onNewChat,
  onRenameChat,
  onDeleteChat,
}: ChatSidebarProps) => {
  const {t} = useTranslation();
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingChatId, setDeletingChatId] = useState<string | null>(null);

  const handleStartRename = useCallback((chatId: string, currentTitle: string) => {
    setRenamingChatId(chatId);
    setRenameValue(currentTitle);
  }, []);

  const handleCancelRename = useCallback(() => {
    setRenamingChatId(null);
    setRenameValue('');
  }, []);

  const handleConfirmRename = useCallback(() => {
    if (!renamingChatId || !renameValue.trim()) {
      handleCancelRename();
      return;
    }
    onRenameChat(renamingChatId, renameValue.trim());
    setRenamingChatId(null);
    setRenameValue('');
  }, [renamingChatId, renameValue, onRenameChat, handleCancelRename]);

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleConfirmRename();
      }
      if (e.key === 'Escape') {
        handleCancelRename();
      }
    },
    [handleConfirmRename, handleCancelRename],
  );

  const handleConfirmDelete = useCallback(() => {
    if (deletingChatId) {
      onDeleteChat(deletingChatId);
      setDeletingChatId(null);
    }
  }, [deletingChatId, onDeleteChat]);

  const handleCancelDelete = useCallback(() => {
    setDeletingChatId(null);
  }, []);

  return (
    <SidebarRoot>
      <NewChatButtonContainer>
        <BlueButton size='medium' onClick={onNewChat} fullWidth>
          <AddIcon />
          <Trans>Новый чат</Trans>
        </BlueButton>
      </NewChatButtonContainer>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      <ChatList>
        {chats.map(chat => {
          const isActive = chat.id === activeChatId;
          const isRenaming = chat.id === renamingChatId;
          const chatTitle = chat.title || t('Без названия');

          return (
            <ChatItem
              key={chat.id}
              isActive={isActive}
              onClick={() => !isRenaming && onSelectChat(chat.id)}
            >
              {isRenaming ? (
                <RenameContainer>
                  <TextField
                    value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={handleRenameKeyDown}
                    size='small'
                    autoFocus
                    fullWidth
                    onClick={e => e.stopPropagation()}
                  />
                  <IconButton
                    size='small'
                    onClick={e => {
                      e.stopPropagation();
                      handleConfirmRename();
                    }}
                  >
                    <CheckIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={e => {
                      e.stopPropagation();
                      handleCancelRename();
                    }}
                  >
                    <CloseIcon fontSize='small' />
                  </IconButton>
                </RenameContainer>
              ) : (
                <>
                  <ChatTitle>{chatTitle}</ChatTitle>
                  <ChatActions className='chat-actions'>
                    <IconButton
                      size='small'
                      onClick={e => {
                        e.stopPropagation();
                        handleStartRename(chat.id, chatTitle);
                      }}
                    >
                      <EditIcon fontSize='small' />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={e => {
                        e.stopPropagation();
                        setDeletingChatId(chat.id);
                      }}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </ChatActions>
                </>
              )}
            </ChatItem>
          );
        })}
      </ChatList>

      <DeleteDialog open={Boolean(deletingChatId)} onClose={handleCancelDelete}>
        <DialogTitle>
          <Trans>Удалить чат</Trans>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Trans>Вы уверены, что хотите удалить этот чат?</Trans>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color='inherit'>
            <Trans>Отмена</Trans>
          </Button>
          <Button onClick={handleConfirmDelete} variant='contained' color='error'>
            <Trans>Удалить</Trans>
          </Button>
        </DialogActions>
      </DeleteDialog>
    </SidebarRoot>
  );
};

export default ChatSidebar;
